import React, { useEffect, useState } from 'react';
import { MinimalTeam } from '../types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import FormAutocompleteBox from '../../Common/FormItems/FormAutocompleteBox';
import { arrayify, downloadFile } from '../../Common/utilities';
import { theme } from '../../Common/styling';
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogContentText,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  CancelOutlined,
  FileDownloadOutlined,
  Handshake,
  HandshakeOutlined,
  DirectionsRun,
  DirectionsRunOutlined,
} from '@mui/icons-material';
import FormFreeDateField from '../../Common/FormFreeItems/FormFreeDateField';
import { getEmployeeTurnoverXlsx } from '../../Statistics/statisticsApi';
import FormFreeCheckbox from '../../Common/FormFreeItems/FormFreeCheckbox';

type FormValues = {
  teams: MinimalTeam[];
  type: 'employment_start' | 'employment_end';
  start_date: string;
  end_date: string;
  show_men: boolean;
  show_women: boolean;
  only_employed: boolean;
  min_employment: number;
  min_employment_unit: string;
  max_employment?: number;
  max_employment_unit: string;
};

type EmployeeStatisticsTabProps = {
  teams?: MinimalTeam[];
  chosenTeams?: MinimalTeam[];
  loadingTeams: boolean;
  loadingChosenTeams: boolean;
  handleClose: () => void;
};

export default function EmployeeStatisticsTab({
  teams,
  chosenTeams,
  loadingTeams,
  loadingChosenTeams,
  handleClose,
}: EmployeeStatisticsTabProps) {
  const [initialValues, setInitialValues] = useState<FormValues>({
    type: 'employment_start',
    teams: [],
    start_date: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
    show_men: true,
    show_women: true,
    only_employed: false,
    min_employment: 1,
    min_employment_unit: 'weeks',
    max_employment: undefined,
    max_employment_unit: 'years',
  });
  const [loadingXlsx, setLoadingXlsx] = useState(false);
  const [exportFinished, setExportFinished] = useState(false);

  useEffect(() => {
    setInitialValues({
      ...initialValues,
      ['teams']: chosenTeams ?? [],
    });
  }, [chosenTeams]);

  const f = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleExport,
    validationSchema: Yup.object({
      teams: Yup.array().min(1, 'Måste välja minst ett team'),
      start_date: Yup.date().typeError('* Ogiltigt datum'),
      end_date: Yup.date().typeError('* Ogiltigt datum'),
      min_employment: Yup.number()
        .typeError('* Måste vara ett nummer')
        .min(1, '* Får ej vara lägre än 1')
        .when(
          ['min_employment_unit', 'max_employment', 'max_employment_unit'],
          (min_employment_unit: string, max_employment: number, max_employment_unit: string, schema: any) => {
            return schema.test({
              test: (min_employment: number) => {
                if (!max_employment) return true;
                if (!min_employment) return true;

                let min_days = min_employment;
                if (min_employment_unit === 'years') {
                  min_days = min_employment * 365;
                } else {
                  if (min_employment_unit === 'weeks') {
                    min_days = min_employment * 7;
                  }
                }

                let max_days = max_employment;
                if (max_employment_unit === 'years') {
                  max_days = max_employment * 365;
                } else {
                  if (max_employment_unit === 'weeks') {
                    max_days = max_employment * 7;
                  }
                }
                return max_days > min_days;
              },
              message: '* Måste vara mindre än anställd max',
            });
          }
        )
        .nullable(),
      max_employment: Yup.number().typeError('* Måste vara ett nummer').min(1, '* Får ej vara lägre än 1').nullable(),
    }),
  });

  async function handleExport(values: FormValues) {
    if (loadingXlsx) return;

    setExportFinished(false);
    setLoadingXlsx(true);

    let min_days = undefined;
    let max_days = undefined;

    if (values.min_employment) {
      if (values.min_employment_unit === 'days') {
        min_days = values.min_employment;
      } else {
        if (values.min_employment_unit === 'weeks') {
          min_days = values.min_employment * 7;
        } else {
          if (values.min_employment_unit === 'years') {
            min_days = values.min_employment * 365;
          }
        }
      }
    }

    if (values.max_employment) {
      if (values.max_employment_unit === 'days') {
        max_days = values.max_employment;
      } else {
        if (values.max_employment_unit === 'weeks') {
          max_days = values.max_employment * 7;
        } else {
          if (values.max_employment_unit === 'years') {
            max_days = values.max_employment * 365;
          }
        }
      }
    }

    await getEmployeeTurnoverXlsx({
      team_ids: values.teams.map((t) => t.id),
      type: values.type,
      start_date: values.start_date,
      end_date: values.end_date,
      show_men: values.show_men,
      show_women: values.show_women,
      only_employed: values.only_employed,
      min_days: min_days,
      max_days: max_days,
    })
      .then(() => {
        setExportFinished(true);
      })
      .finally(() => {
        setLoadingXlsx(false);
      });
  }

  const durationString = (unit: string, duration?: number): string => {
    if (duration) {
      if (duration === 1) {
        return `${duration} ${unit === 'days' ? 'dag' : unit === 'weeks' ? 'vecka' : 'år'}`;
      } else {
        return `${duration} ${unit === 'days' ? 'dagar' : unit === 'weeks' ? 'veckor' : 'år'}`;
      }
    } else {
      return `${unit === 'days' ? 'dagar' : unit === 'weeks' ? 'veckor' : 'år'}`;
    }
  };

  const genderString = (show_men: boolean, show_women: boolean): string => {
    if (show_men && !show_women) {
      return 'män';
    } else {
      if (show_women && !show_men) {
        return 'kvinnor';
      } else {
        return 'användare';
      }
    }
  };

  return (
    <div>
      <form onSubmit={f.handleSubmit} className="pt-2">
        <div className="h-106">
          <div className="flex gap-2 items-start">
            <FormControl className="grow">
              <FormAutocompleteBox
                type="multiple"
                label="Välj team"
                options={teams ?? []}
                values={f.values.teams ?? []}
                onChange={(_, values) => f.setFieldValue('teams', arrayify(values))}
                renderTags="simple"
                loading={loadingTeams || loadingChosenTeams}
                error={(f.errors.teams as string) ?? null}
              />
            </FormControl>
            <Button variant="outlined" onClick={teams && (() => f.setFieldValue('teams', teams))} className="h-[53px]">
              Välj alla
            </Button>
          </div>

          <div className="w-full mt-4 text-neutral-600">
            Export av personalomsättning utifrån anställningsdatum eller avslutningdatum, med hänsyn till
            anställningstid. Filen exporteras i .xlsx format.
          </div>

          <div className="w-full mt-8 text-neutral-600">
            Filtrera{' '}
            <span className={`text-[${theme.themeGreen}] font-bold`}>
              {genderString(f.values.show_men, f.values.show_women)}
            </span>{' '}
            vars{' '}
            <span className={`text-[${theme.themeGreen}] font-bold`}>
              {f.values.type === 'employment_start' ? 'anställnings' : 'avslutnings'}
            </span>
            -datum{' '}
            <span className={`text-[${theme.themeGreen}] font-bold`}>
              {!f.values.start_date && !f.values.end_date
                ? `baserat på ${f.values.type === 'employment_start' ? 'anställnings' : 'avslutnings'}tid`
                : f.values.start_date && !f.values.end_date
                ? `skedde tidigast ${f.values.start_date}`
                : !f.values.start_date && f.values.end_date
                ? `skedde senast ${f.values.end_date}`
                : 'faller inom intervallet'}
            </span>
          </div>

          <div className="w-full grid grid-cols-4 gap-4 mt-4">
            <Button
              className="h-13"
              fullWidth
              variant={f.values.type === 'employment_start' ? 'contained' : 'outlined'}
              color="primary"
              // disabled={false}
              endIcon={f.values.type === 'employment_start' ? <Handshake /> : <HandshakeOutlined />}
              onClick={() => f.setFieldValue('type', 'employment_start')}
            >
              Anställning
            </Button>

            <Button
              className="h-13"
              fullWidth
              variant={f.values.type === 'employment_end' ? 'contained' : 'outlined'}
              color="primary"
              endIcon={f.values.type === 'employment_end' ? <DirectionsRun /> : <DirectionsRunOutlined />}
              onClick={() => {
                f.setFieldValue('type', 'employment_end');
                f.setFieldValue('only_employed', false);
              }}
            >
              Avslutning
            </Button>

            <div className="flex flex-col">
              <FormFreeDateField
                label={f.values.type === 'employment_start' ? 'Anställningsdatum f.o.m.' : 'Avslutningsdatum f.o.m.'}
                onlyValid={false}
                size={null}
                value={f.values.start_date ? dayjs(f.values.start_date) : null}
                maxDate={f.values.end_date ? dayjs(f.values.end_date) : dayjs()}
                onChange={(day) => f.setFieldValue('start_date', day?.format('YYYY-MM-DD'))}
                error={f.errors.start_date}
                sx={{
                  '& .MuiIconButton-root': {
                    color: '#439676 !important',
                  },
                  '& .Mui-disabled .MuiOutlinedInput-input': {
                    WebkitTextFillColor: '#43967680 !important',
                  },
                  '& .MuiOutlinedInput-input': {
                    WebkitTextFillColor: '#439676 !important',
                  },
                  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#43967680 !important',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#439676 !important',
                  },
                  '& .Mui-disabled.MuiInputLabel-root': {
                    color: '#43967680 !important',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#439676 !important',
                  },
                }}
              />
            </div>
            <div className="flex flex-col">
              <FormFreeDateField
                label={f.values.type === 'employment_start' ? 'Anställningsdatum t.o.m.' : 'Avslutningsdatum t.o.m.'}
                onlyValid={false}
                size={null}
                value={f.values.end_date ? dayjs(f.values.end_date) : null}
                minDate={f.values.start_date ? dayjs(f.values.start_date) : undefined}
                maxDate={dayjs()}
                onChange={(day) => f.setFieldValue('end_date', day?.format('YYYY-MM-DD'))}
                error={f.errors.end_date}
                sx={{
                  '& .MuiIconButton-root': {
                    color: '#439676 !important',
                  },
                  '& .Mui-disabled .MuiOutlinedInput-input': {
                    WebkitTextFillColor: '#43967680 !important',
                  },
                  '& .MuiOutlinedInput-input': {
                    WebkitTextFillColor: '#439676 !important',
                  },
                  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#43967680 !important',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#439676 !important',
                  },
                  '& .Mui-disabled.MuiInputLabel-root': {
                    color: '#43967680 !important',
                  },
                  '& .MuiInputLabel-root': {
                    color: '#439676 !important',
                  },
                }}
              />
            </div>
          </div>

          <div className="w-full flex gap-32 mt-4">
            <div className="flex flex-col">
              <div className="flex gap-4 xl:gap-8">
                <div className="!w-1 mt-[5px]">
                  <FormFreeCheckbox name="show_men" label="" values={f.values} onChange={f.handleChange} />
                </div>
                <div className="flex mt-[16px] 2xl:mt-[11px] text-sm text-neutral-600 2xl:text-lg">
                  <span>Inkludera män</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex gap-4 xl:gap-8">
                <div className="!w-1 mt-[5px]">
                  <FormFreeCheckbox name="show_women" label="" values={f.values} onChange={f.handleChange} />
                </div>
                <div className="flex mt-[16px] 2xl:mt-[11px] text-sm text-neutral-600 2xl:text-lg">
                  <span>Inkludera kvinnor</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex gap-4 xl:gap-8">
                <div className="!w-1 mt-[5px]">
                  <FormFreeCheckbox
                    name="only_employed"
                    label=""
                    values={f.values}
                    onChange={f.handleChange}
                    disabled={f.values.type === 'employment_end'}
                  />
                </div>
                <div className="flex mt-[16px] 2xl:mt-[11px] text-sm text-neutral-600 2xl:text-lg">
                  <span className={f.values.type === 'employment_end' ? 'text-[#BDBDBD]' : ''}>
                    Endast pågående anställning
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full mt-8 text-neutral-600">
            Filtrera{' '}
            <span className={`text-[${theme.themeGreen}] font-bold`}>
              {genderString(f.values.show_men, f.values.show_women)}
            </span>{' '}
            vars anställningstid{' '}
            <span className={`text-[${theme.themeGreen}] font-bold`}>
              {f.values.min_employment && !f.values.max_employment
                ? `är minst ${durationString(f.values.min_employment_unit, f.values.min_employment)}`
                : !f.values.min_employment && f.values.max_employment
                ? `är max ${durationString(f.values.max_employment_unit, f.values.max_employment)}`
                : `varierar mellan ${durationString(
                    f.values.min_employment_unit,
                    f.values.min_employment
                  )} och ${durationString(f.values.max_employment_unit, f.values.max_employment)}`}
            </span>
          </div>

          <div className="flex mt-4 gap-4">
            <div className="flex flex-col !w-48">
              <TextField
                fullWidth
                name="min_employment"
                variant="outlined"
                label="Anställd minst"
                value={f.values.min_employment}
                error={!!f.errors.min_employment}
                onChange={f.handleChange}
                sx={{
                  // Root class for the input field
                  '& .MuiOutlinedInput-root': {
                    color: theme.themeGreen,
                    // Class for the border around the input field
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                  },
                  // Class for the label of the input field
                  '& .MuiInputLabel-outlined': {
                    color: theme.themeGreen,
                  },
                }}
                InputLabelProps={{ shrink: true }}
                helperText={f.errors.min_employment ?? ' '}
              />
            </div>

            <div className="flex flex-col !w-24">
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    '&.MuiInputLabel-shrink': {
                      color: theme.themeGreen,
                    },
                  }}
                >
                  Tidsenhet
                </InputLabel>
                <Select
                  name="min_employment_unit"
                  label="Tidsenhet"
                  value={f.values.min_employment_unit}
                  onChange={f.handleChange}
                  sx={{
                    color: theme.themeGreen,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '.MuiSvgIcon-root ': {
                      fill: theme.themeGreen,
                    },
                  }}
                >
                  <MenuItem value="days" key="days">
                    Dagar
                  </MenuItem>
                  <MenuItem value="weeks" key="weeks">
                    Veckor
                  </MenuItem>
                  <MenuItem value="years" key="years">
                    År
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="flex flex-col !w-48">
              <TextField
                fullWidth
                name="max_employment"
                variant="outlined"
                label="Anställd max"
                value={f.values.max_employment}
                error={!!f.errors.max_employment}
                onChange={f.handleChange}
                sx={{
                  // Root class for the input field
                  '& .MuiOutlinedInput-root': {
                    color: theme.themeGreen,
                    // Class for the border around the input field
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                  },
                  // Class for the label of the input field
                  '& .MuiInputLabel-outlined': {
                    color: theme.themeGreen,
                  },
                }}
                InputLabelProps={{ shrink: true }}
                helperText={f.errors.max_employment ?? ' '}
              />
            </div>
            <div className="flex flex-col !w-24">
              <FormControl fullWidth>
                <InputLabel
                  sx={{
                    '&.MuiInputLabel-shrink': {
                      color: theme.themeGreen,
                    },
                  }}
                >
                  Tidsenhet
                </InputLabel>
                <Select
                  variant="outlined"
                  name="max_employment_unit"
                  label="Tidsenhet"
                  value={f.values.max_employment_unit}
                  onChange={f.handleChange}
                  sx={{
                    color: theme.themeGreen,
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.themeGreen,
                    },
                    '.MuiSvgIcon-root ': {
                      fill: theme.themeGreen,
                    },
                  }}
                >
                  <MenuItem value="days" key="days">
                    Dagar
                  </MenuItem>
                  <MenuItem value="weeks" key="weeks">
                    Veckor
                  </MenuItem>
                  <MenuItem value="years" key="years">
                    År
                  </MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </div>

        <hr className="my-4 h-0.5 border-t-0 bg-neutral-100" />

        <div className="flex justify-between mt-10 h-12">
          <div className="flex w-60">
            <Button fullWidth variant="outlined" onClick={handleClose} color="error" endIcon={<CancelOutlined />}>
              Stäng
            </Button>
          </div>

          {exportFinished && (
            <div className="flex flex-col w-100 text-xs text-menuGreen text-center">
              <div className="mt-1">
                Skapad exportfil <span className="font-bold">mailas automatiskt</span> så fort den är redo...
              </div>
              <div className="mt-2">Du kan stänga fönstret!</div>
            </div>
          )}

          <div className="flex w-60">
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingXlsx || !f.isValid}
              endIcon={loadingXlsx ? <CircularProgress size="20px" /> : <FileDownloadOutlined />}
            >
              Skapa exportfil
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
